import React, { useContext } from 'react';
import { EmptyData, ITableContainerProps, Table as T } from '@design-system';
import { nanoid } from 'nanoid';
import { formatDate } from 'helpers';
import fp from 'lodash/fp';
import { ITeamFolder } from '@cincel/client';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { WLContext } from '@contexts';
import { cx } from '@emotion/css';
import { useRouter } from 'hooks/useRouter';
import { Link } from 'react-router-dom';

const HEADERS = [
  translations.COMMON.TABLE_HEADING_NAME,
  translations.COMMON.TABLE_HEADING_STATUS,
  translations.COMMON.TABLE_HEADING_CREATED_AT,
];

export interface IListingProps extends ITableContainerProps {
  /**
   * Folders listing.
   */
  data?: ITeamFolder[];
  /**
   * On delete action.
   */
  onDelete: (contact: string) => void;
  /**
   * Callback function invoked when the page changes.
   */
  onPageChange: (page: number) => void;
  /**
   * Callback function invoked with the page-size changes.
   */
  onPageSizeChange: (size: number) => void;
  /**
   * On update action.
   */
  onUpdate: (folder: ITeamFolder) => void;
  /**
   * A numeric value indicating the current page.
   */
  page: number;
  /**
   * A numeric value indicating the page size.
   */
  pageSize: number;
  /**
   * Total registries count.
   */
  total: number;
}

export const Listing: React.FC<IListingProps> = (props): JSX.Element => {
  const {
    data,
    onDelete,
    onPageChange,
    onPageSizeChange,
    onUpdate,
    page,
    pageSize,
    total,
    ...rest
  } = props;

  const { color } = useContext(WLContext);

  const { t } = useTranslation();

  const router = useRouter();

  return (
    <>
      <T.Container {...rest}>
        {!fp.isEmpty(data) ? (
          <T>
            <T.Thead>
              <T.Tr>
                {HEADERS.map((header) => (
                  <T.Th key={nanoid()} scope="col">
                    {t(header)}
                  </T.Th>
                ))}
                <T.Th className="min-w-[60px]" scope="col" />
              </T.Tr>
            </T.Thead>

            <T.Tbody>
              {data?.map((item: ITeamFolder) => (
                <T.Tr key={nanoid()} className="even:bg-gray-25">
                  <T.Td className="lg:min-w-[320px]">
                    <Link
                      className={cx('font-medium', {
                        'cursor-not-allowed': !fp.isNil(item?.deleted_at),
                      })}
                      to={
                        fp.isNil(item?.deleted_at)
                          ? `/teams/${router?.query?.team}/folders/${item?.uuid}/documents`
                          : '#'
                      }>
                      {item?.name}
                    </Link>
                  </T.Td>

                  <T.Td className="lg:min-w-[256px]">
                    {t(
                      fp.isNil(item?.deleted_at)
                        ? translations.COMMON.ACTIVE
                        : translations.COMMON.INACTIVE,
                    )}
                  </T.Td>

                  <T.Td>{formatDate(item?.created_at)}</T.Td>

                  <T.TdActions
                    brandColor={color}
                    isOwner
                    onDelete={() => onDelete(item.uuid)}
                    onUpdate={() => onUpdate(item)}
                  />
                </T.Tr>
              ))}
            </T.Tbody>
          </T>
        ) : (
          <EmptyData>{t(translations.FOLDERS_PAGES.TABLE_EMPTY)}</EmptyData>
        )}
      </T.Container>

      <T.Pagination
        className="mt-4"
        currentPage={page}
        onPageChange={onPageChange}
        onSizeChange={onPageSizeChange}
        pageSize={pageSize}
        primaryColor={color}
        totalCount={total}
      />
    </>
  );
};
